import { create } from 'zustand'
import type { PermissionMap, SystemRole } from '@/types/permission.types'
import {
  rolePermissionsService,
  type ConfigurableRole,
  type RolePermissionsPayload,
} from '@/modules/users/services/role-permissions.service'
import { rolePermissionKeys } from '@/modules/users/hooks/useRolePermissions'
import { buildAllPermissions } from '@/constants/permissions'
import { getErrorMessage } from '@/services/api/errors'
import { queryClient } from '@/lib/query-client'

export type RolePermissionConfig = Record<SystemRole, PermissionMap>

function emptyConfig(): RolePermissionConfig {
  return {
    super_admin: {},
    manager: {},
    assistant: {},
    salesman: {},
  }
}

function fromPayload(payload: RolePermissionsPayload): RolePermissionConfig {
  return {
    super_admin: {},
    manager: payload.manager ?? {},
    assistant: payload.assistant ?? {},
    salesman: payload.salesman ?? {},
  }
}

interface PermissionStoreState {
  rolePermissions: RolePermissionConfig
  selectedRole: ConfigurableRole
  isDirty: boolean
  isLoading: boolean
  isSaving: boolean
  error: string | null
  setSelectedRole: (role: SystemRole) => void
  setRolePermission: (
    role: SystemRole,
    key: keyof PermissionMap,
    value: boolean,
  ) => void
  setModulePermissions: (
    role: SystemRole,
    permissions: PermissionMap,
  ) => void
  replaceRolePermissions: (role: SystemRole, map: PermissionMap) => void
  fetch: () => Promise<void>
  save: () => Promise<void>
  reset: () => Promise<void>
  reload: () => Promise<void>
}

export const usePermissionStore = create<PermissionStoreState>((set, get) => ({
  rolePermissions: emptyConfig(),
  selectedRole: 'manager',
  isDirty: false,
  isLoading: false,
  isSaving: false,
  error: null,

  setSelectedRole: (role) => {
    if (role === 'super_admin') return
    set({ selectedRole: role })
  },

  setRolePermission: (role, key, value) => {
    if (role === 'super_admin') return
    const current = get().rolePermissions
    set({
      rolePermissions: {
        ...current,
        [role]: { ...current[role], [key]: value },
      },
      isDirty: true,
    })
  },

  setModulePermissions: (role, permissions) => {
    if (role === 'super_admin') return
    const current = get().rolePermissions
    set({
      rolePermissions: {
        ...current,
        [role]: { ...current[role], ...permissions },
      },
      isDirty: true,
    })
  },

  replaceRolePermissions: (role, map) => {
    if (role === 'super_admin') return
    const current = get().rolePermissions
    set({
      rolePermissions: { ...current, [role]: map },
      isDirty: true,
    })
  },

  fetch: async () => {
    set({ isLoading: true, error: null })
    try {
      const payload = await queryClient.fetchQuery({
        queryKey: rolePermissionKeys.list(),
        queryFn: ({ signal }) => rolePermissionsService.list({ signal }),
      })
      set({
        rolePermissions: fromPayload(payload),
        isDirty: false,
        isLoading: false,
      })
    } catch (error) {
      set({
        isLoading: false,
        error: getErrorMessage(error, 'Failed to load role permissions'),
      })
      throw error
    }
  },

  save: async () => {
    const { rolePermissions, selectedRole } = get()
    set({ isSaving: true, error: null })
    try {
      // PUT requires a FULL boolean map for every catalog key.
      const fullMap = buildAllPermissions(false)
      const current = rolePermissions[selectedRole] ?? {}
      for (const key of Object.keys(fullMap) as Array<keyof PermissionMap>) {
        fullMap[key] = Boolean(current[key])
      }
      await rolePermissionsService.update(selectedRole, fullMap)
      await queryClient.invalidateQueries({ queryKey: rolePermissionKeys.list() })
      const payload = await rolePermissionsService.list()
      set({
        rolePermissions: fromPayload(payload),
        isDirty: false,
        isSaving: false,
      })
    } catch (error) {
      set({
        isSaving: false,
        error: getErrorMessage(error, 'Failed to save role permissions'),
      })
      throw error
    }
  },

  reset: async () => {
    set({ isSaving: true, error: null })
    try {
      const payload = await rolePermissionsService.reset()
      await queryClient.invalidateQueries({ queryKey: rolePermissionKeys.list() })
      set({
        rolePermissions: fromPayload(payload),
        isDirty: false,
        isSaving: false,
      })
    } catch (error) {
      set({
        isSaving: false,
        error: getErrorMessage(error, 'Failed to reset role permissions'),
      })
      throw error
    }
  },

  reload: async () => {
    await get().fetch()
  },
}))
